/**
 * DesktopCarousel Component
 * Desktop-specific implementation of the BrandsCarousel with a horizontal layout
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import BrandCard from './BrandCard';

// Import styles directly from their source files
import {
  commonContainerStyles,
  commonSeoDescriptionStyles,
  commonTitleStyles
} from '../styles/common';

import {
  desktopContainerStyles,
  desktopTitleStyles,
  desktopCarouselContainerStyles,
  desktopCarouselWrapperStyles,
  desktopBrandCardStyles
} from '../styles/desktop';

// --- Framer Motion Variants (can be shared or customized) ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05, // Even faster stagger for desktop?
    },
  },
};

const itemVariants = {
  hidden: { x: -20, opacity: 0 }, // Slide in from left
  visible: { x: 0, opacity: 1 },
};
// --- End Framer Motion Variants ---

const DesktopCarousel = ({ 
  brands, 
  showTitle = true,
  currentBrandId = null
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  
  // Simulate API loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // Simulate 1 second loading time
    return () => clearTimeout(timer);
  }, []);
  
  // --- Skeleton Loader ---
  const renderSkeletons = () => {
    const skeletonCount = 6; // Show 6 placeholders
    // Estimate card width and height for desktop
    const cardWidth = desktopBrandCardStyles.width || '180px'; 
    const cardHeight = desktopBrandCardStyles.height || '150px'; 

    return (
      <Stack 
        direction="row" 
        spacing={desktopBrandCardStyles.marginRight || 2} 
        sx={desktopCarouselWrapperStyles} // Apply similar wrapper styles
      >
        {[...Array(skeletonCount)].map((_, index) => (
          <Skeleton 
            key={index}
            variant="rectangular" 
            width={cardWidth}
            height={cardHeight} 
            sx={{ borderRadius: 1 }}
            animation="wave"
          />
        ))}
      </Stack>
    );
  };
  // --- End Skeleton Loader ---

  return (
    <Box sx={{ ...commonContainerStyles, ...desktopContainerStyles }}>
      <Container maxWidth="lg">
        {showTitle && (
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ ...commonTitleStyles, ...desktopTitleStyles }}
          >
            {t('dashboard.brandsCarousel.title')}
          </Typography>
        )}
        
        {/* Hidden SEO description */}
        <Typography 
          component="p" 
          sx={commonSeoDescriptionStyles}
        >
          {t('dashboard.brandsCarousel.description')}
        </Typography>
        
        <Box sx={desktopCarouselContainerStyles}>
          {isLoading ? renderSkeletons() : (
            <Box 
              component={motion.div}
              sx={desktopCarouselWrapperStyles}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {brands.map((brand, index) => (
                <Box
                  component={motion.div}
                  key={brand.id}
                  variants={itemVariants}
                  // marginRight handled by wrapper spacing or BrandCard style, check common styles
                >
                  <BrandCard 
                    brand={brand} 
                    deviceType="desktop"
                    isFirstItem={index === 0}
                    isCurrentBrand={currentBrandId === brand.id}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

DesktopCarousel.propTypes = {
  brands: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired
    })
  ).isRequired,
  showTitle: PropTypes.bool,
  currentBrandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

DesktopCarousel.defaultProps = {
  showTitle: true,
  currentBrandId: null
};

export default memo(DesktopCarousel);